import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { IFeatureFlagsState, IFeatureFlag } from '../../../types/featureFlags.types';
import { featureFlagsService } from '../../../services/api/featureFlags.service';
import { getApiErrorMessage } from '../../../utils/errorHandling';

// Initial state
const initialState: IFeatureFlagsState = {
  flags: {},
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Async Thunks
 */

export const fetchFeatureFlags = createAsyncThunk(
  'featureFlags/fetchFlags',
  async (_, { rejectWithValue }) => {
    try {
      const flags = await featureFlagsService.getFlags();
      return flags;
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch feature flags'));
    }
  }
);

/**
 * Feature Flags Slice
 */

const featureFlagsSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {
    // Set a specific flag (for testing/override)
    setFlag: (state, action: PayloadAction<{ key: string; enabled: boolean }>) => {
      state.flags[action.payload.key] = action.payload.enabled;
    },
    // Clear all flags
    clearFlags: (state) => {
      state.flags = {};
      state.lastFetched = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch feature flags
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action: PayloadAction<IFeatureFlag[]>) => {
        state.isLoading = false;
        state.flags = action.payload.reduce((acc, flag) => {
          acc[flag.key] = flag.enabled;
          return acc;
        }, {} as Record<string, boolean>);
        state.lastFetched = new Date().toISOString();
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

/**
 * Actions
 */
export const { setFlag, clearFlags } = featureFlagsSlice.actions;

/**
 * Selectors
 */
export const selectFeatureFlags = (state: { featureFlags: IFeatureFlagsState }) => state.featureFlags;
export const selectIsFeatureEnabled = (key: string) => (state: { featureFlags: IFeatureFlagsState }) =>
  state.featureFlags.flags[key] === true;

/**
 * Reducer
 */
export default featureFlagsSlice.reducer;

